import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileImage, Eye, Trash2, MoveHorizontal, ArrowUp, ArrowDown, GripVertical, X, Info } from 'lucide-react';
import { usePropertyImages } from '../../../../hooks/usePropertyImages';
import { toast } from 'react-toastify';

interface ImagesSectionProps {
  propertyId?: string;
  isEditing: boolean;
}

const ImagesSection: React.FC<ImagesSectionProps> = ({ propertyId, isEditing }) => {
  const {
    images,
    uploadProgress,
    primaryImageId,
    localImageUrls,
    error,
    dragInProgress,
    handleSetPrimary,
    handleRemoveImage,
    handleReorderImages,
    onDrop
  } = usePropertyImages(propertyId);

  const [draggedImageId, setDraggedImageId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(true);

  // Mostra un tutorial all'inizio se ci sono più immagini
  useEffect(() => {
    if (images.length > 1) {
      toast.info("Per cambiare l'ordine delle immagini, trascina e rilascia le thumbnail o usa i pulsanti freccia", {
        autoClose: 5000,
        position: "top-center",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [images.length > 1]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 10485760, // 10MB (era 5242880 - 5MB)
    disabled: !isEditing || !propertyId
  });

  // Drag handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, imageId: string) => {
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Crea un'immagine fantasma personalizzata per un migliore UX
    const ghostImg = document.createElement('div');
    const img = images.find(img => img.id === imageId);
    
    if (img) {
      const imgSrc = img.isNew ? localImageUrls[img.id] : img.url;
      ghostImg.innerHTML = `
        <div style="
          width: 100px; 
          height: 100px; 
          border-radius: 8px; 
          overflow: hidden;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
          background-color: #FFF;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #1D2B4E;
        ">
          <div style="
            position: absolute;
            top: 0;
            left: 0;
            background-color: #1D2B4E;
            color: white;
            padding: 2px 6px;
            font-weight: bold;
            font-size: 14px;
          ">${images.findIndex(i => i.id === imageId) + 1}</div>
          <img src="${imgSrc}" style="width: 100%; height: 100%; object-fit: cover;">
        </div>`;
    } else {
      ghostImg.innerHTML = `<div class="w-20 h-20 bg-primary/80 rounded-lg flex items-center justify-center text-white font-bold">
        <span>Sposta</span>
      </div>`;
    }
    
    document.body.appendChild(ghostImg);
    const ghost = ghostImg.firstElementChild as HTMLElement;
    
    if (ghost) {
      e.dataTransfer.setDragImage(ghost, 50, 50);
      setTimeout(() => {
        document.body.removeChild(ghostImg);
      }, 0);
    }

    // Mostra un messaggio toast che suggerisce dove rilasciare
    toast.info("Rilascia l'immagine nella posizione desiderata", {
      autoClose: 3000,
      position: "top-center", 
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      toastId: "drag-helper"
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, imageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedImageId !== imageId) {
      setDropTargetId(imageId);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetId(null);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggedImageId(null);
    setDropTargetId(null);
    toast.dismiss("drag-helper");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetImageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedImageId || draggedImageId === targetImageId) return;

    // Trova gli indici
    const sourceIndex = images.findIndex(img => img.id === draggedImageId);
    const targetIndex = images.findIndex(img => img.id === targetImageId);
    
    if (sourceIndex !== -1 && targetIndex !== -1) {
      handleReorderImages(sourceIndex, targetIndex);
      
      // Mostra un toast di conferma
      toast.success(`Immagine spostata con successo da posizione ${sourceIndex + 1} a ${targetIndex + 1}`, {
        autoClose: 2000,
        position: "bottom-center"
      });
    }
    
    setDraggedImageId(null);
    setDropTargetId(null);
    toast.dismiss("drag-helper");
  };

  // Gestori di click per riordinare (come fallback per dispositivi mobili)
  const handleMoveUp = (imageId: string) => {
    const index = images.findIndex(img => img.id === imageId);
    if (index > 0) {
      handleReorderImages(index, index - 1);
      toast.success(`Immagine spostata in su`, {
        autoClose: 1500,
        position: "bottom-center"
      });
    }
  };

  const handleMoveDown = (imageId: string) => {
    const index = images.findIndex(img => img.id === imageId);
    if (index < images.length - 1) {
      handleReorderImages(index, index + 1);
      toast.success(`Immagine spostata in giù`, {
        autoClose: 1500,
        position: "bottom-center"
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
        <FileImage className="h-5 w-5 mr-2 text-primary" />
        Immagini dell'Immobile
      </h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
          {error}
        </div>
      )}
      
      <div>
        <div 
          {...getRootProps()} 
          className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${
            !isEditing || !propertyId 
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
              : isDragActive 
                ? 'border-primary border-dashed bg-primary/5' 
                : 'border-gray-300 border-dashed'
          } rounded-md`}
        >
          <div className="space-y-1 text-center">
            <FileImage className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                className={`relative ${
                  !isEditing || !propertyId 
                    ? 'cursor-not-allowed text-gray-400'
                    : 'cursor-pointer text-primary hover:text-primary-light'
                }`}
              >
                <span>Carica immagini</span>
                <input {...getInputProps()} />
              </label>
              <p className="pl-1">o trascina qui</p>
            </div>
            <p className="text-xs text-gray-500">PNG, JPG, GIF fino a 10MB — le immagini vengono ottimizzate automaticamente</p>
            {!isEditing && (
              <p className="text-xs text-red-500 mt-2">
                Salva l'immobile prima di caricare le immagini
              </p>
            )}
          </div>
        </div>
        
        {/* Images preview */}
        {images.length > 0 && (
          <div className="mt-6">
            {/* Intestazione e istruzioni */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
              <h4 className="font-medium text-gray-700">Immagini ({images.length})</h4>
              
              {/* Istruzione prominente di trascinamento */}
              <div className="flex items-center bg-blue-100 px-3 py-1.5 rounded-md text-blue-700 font-medium animate-pulse">
                <MoveHorizontal className="h-5 w-5 mr-1.5" />
                <span>TRASCINA le immagini per riordinarle</span>
              </div>
            </div>

            {showTutorial && images.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4 text-sm relative">
                <button 
                  onClick={() => setShowTutorial(false)}
                  className="absolute top-2 right-2 text-blue-500 hover:text-blue-700"
                >
                  <X className="h-5 w-5" />
                </button>
                
                <div className="flex items-start mb-3">
                  <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <h5 className="font-medium text-blue-800">Come riordinare le immagini:</h5>
                </div>
                
                <ul className="space-y-3 pl-7 text-blue-700">
                  <li className="flex items-center">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-2 text-sm font-bold">1</div>
                    <strong className="mr-1">Metodo 1:</strong> Clicca e trascina un'immagine sopra un'altra per cambiare l'ordine
                  </li>
                  <li className="flex items-center">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-2 text-sm font-bold">2</div>
                    <strong className="mr-1">Metodo 2:</strong> Usa i pulsanti <ArrowUp className="h-4 w-4 inline mx-1 text-blue-700" /> e <ArrowDown className="h-4 w-4 inline mx-1 text-blue-700" /> per spostare le immagini
                  </li>
                </ul>
                
                <div className="mt-3 flex bg-white p-2 rounded-lg">
                  <div className="w-16 h-16 bg-blue-100 border-2 border-blue-300 rounded flex items-center justify-center mr-3 relative flex-shrink-0">
                    <span className="absolute top-0 left-0 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">1</span>
                    <GripVertical className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="text-blue-700">
                    <p className="text-xs font-medium">Ogni immagine può essere:</p>
                    <ul className="text-xs list-disc pl-4 mt-1">
                      <li>Trascinata con il mouse (drag and drop)</li>
                      <li>Spostata con i pulsanti freccia in alto a destra</li>
                      <li>Impostata come principale con il pulsante <Eye className="h-3 w-3 inline" /></li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {images.map((img, index) => (
                <div 
                  key={img.id} 
                  className={`relative group border-2 rounded-lg overflow-hidden shadow-sm transition-all cursor-grab active:cursor-grabbing ${
                    img.id === primaryImageId ? 'ring-2 ring-primary border-primary' : 
                    img.id === draggedImageId ? 'opacity-50 scale-95 shadow-none border-dashed border-gray-400' : 
                    img.id === dropTargetId ? 'ring-2 ring-secondary border-secondary shadow-lg scale-105 border-dashed' : 
                    'border-gray-200 hover:border-primary hover:shadow-md'
                  }`}
                  draggable={isEditing}
                  onDragStart={(e) => handleDragStart(e, img.id)}
                  onDragOver={(e) => handleDragOver(e, img.id)}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, img.id)}
                >
                  <div className="relative aspect-square">
                    <img
                      src={img.isNew ? localImageUrls[img.id] : img.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay per drag and drop - SEMPRE VISIBILE per essere più evidente */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="absolute top-0 left-0 right-0 p-2 text-center">
                        <span className="text-xs font-bold bg-secondary/80 text-white px-3 py-1 rounded-full">
                          Trascina per riordinare
                        </span>
                      </div>
                      
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-white/20 rounded-full p-3">
                          <GripVertical className="h-8 w-8 text-white drop-shadow-lg" />
                        </div>
                      </div>
                    </div>
                    
                    {uploadProgress[img.id] !== undefined && uploadProgress[img.id] < 100 && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="w-full max-w-[80%]">
                          <div className="h-2.5 bg-gray-300 rounded-full">
                            <div 
                              className="h-full bg-primary rounded-full" 
                              style={{ width: `${uploadProgress[img.id]}%` }}
                            ></div>
                          </div>
                          <p className="text-white text-sm mt-2 text-center font-medium">{uploadProgress[img.id]}%</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Numero di posizione grande e chiaro */}
                    <div className="absolute top-2 left-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-lg font-bold border-2 border-white shadow-md">
                      {index + 1}
                    </div>
                  </div>
                  
                  {/* Pulsanti di controllo posizionamento */}
                  <div className="absolute right-2 top-2 flex flex-col gap-2">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => handleMoveUp(img.id)}
                        className="w-8 h-8 rounded-full bg-white shadow-lg text-primary hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
                        title="Sposta su"
                      >
                        <ArrowUp className="h-5 w-5" />
                      </button>
                    )}
                    {index < images.length - 1 && (
                      <button
                        type="button"
                        onClick={() => handleMoveDown(img.id)}
                        className="w-8 h-8 rounded-full bg-white shadow-lg text-primary hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
                        title="Sposta giù"
                      >
                        <ArrowDown className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                  
                  {/* Barra di controlli inferiore */}
                  <div className="absolute inset-x-0 bottom-0 p-2 flex justify-between bg-gradient-to-t from-black/70 to-transparent">
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(img.id)}
                      className={`p-1.5 rounded-full ${
                        img.id === primaryImageId 
                          ? 'bg-primary text-white' 
                          : 'bg-white/80 text-gray-700 hover:bg-white'
                      }`}
                      title="Imposta come principale"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id)}
                      className="p-1.5 rounded-full bg-red-500/80 text-white hover:bg-red-500"
                      title="Rimuovi immagine"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  
                  {/* Indicatore di immagine principale */}
                  {img.id === primaryImageId && (
                    <div className="absolute bottom-0 left-0 right-0 bg-primary text-white text-xs py-1 text-center font-medium">
                      Immagine principale
                    </div>
                  )}

                  {/* Indicatore di trascinamento attivo */}
                  {img.id === dropTargetId && (
                    <div className="absolute inset-0 border-2 border-dashed border-secondary rounded-lg pointer-events-none"></div>
                  )}
                </div>
              ))}
            </div>

            {/* Suggerimento animato per il drag and drop */}
            {images.length > 1 && (
              <div className="mt-4 bg-blue-50 p-3 rounded-lg border border-blue-100 text-sm text-blue-700">
                <p className="flex items-center font-medium animate-pulse">
                  <span className="mr-2 text-lg">💡</span>
                  <span>
                    <strong>Suggerimento:</strong> L'ordine delle immagini determina come appariranno nella galleria dell'immobile. L'immagine principale (con l'icona <Eye className="h-3.5 w-3.5 inline mx-0.5" />) sarà sempre mostrata per prima.
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagesSection;